import { useEffect, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { compressImageToBase64, ImageTooLargeError } from '../utils/imageCompression';
import '../styles/ChatWidget.css';

function ContactForm({ onSubmit }) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    await onSubmit({
      name: name.trim(),
      subject: subject.trim(),
      email: email.trim(),
      firstMessage: message.trim(),
    });
    setSubmitting(false);
  };

  return (
    <form className="chat-form" onSubmit={handleSubmit}>
      <label className="chat-form__label">Full name</label>
      <input className="chat-form__input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required />

      <label className="chat-form__label">Subject</label>
      <input className="chat-form__input" placeholder="What's this about?" value={subject} onChange={(e) => setSubject(e.target.value)} />

      <label className="chat-form__label">Message</label>
      <textarea className="chat-form__input chat-form__textarea" placeholder="How can we help?" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />

      <label className="chat-form__label">Your email</label>
      <input className="chat-form__input" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />

      <button type="submit" className="chat-form__submit" disabled={submitting}>
        {submitting ? 'Starting chat…' : 'Start chat'}
      </button>
    </form>
  );
}

function MessageBubble({ message, onImageClick }) {
  const isUser = message.sender === 'user';
  return (
    <div className={`chat-bubble-row ${isUser ? 'chat-bubble-row--user' : 'chat-bubble-row--admin'}`}>
      <div className={`chat-bubble ${isUser ? 'chat-bubble--user' : 'chat-bubble--admin'}`}>
        {message.imageBase64 && (
          <img
            src={message.imageBase64}
            alt="Attachment"
            className="chat-bubble__image"
            onClick={() => onImageClick(message.imageBase64)}
          />
        )}
        {message.text && <p className="chat-bubble__text">{message.text}</p>}
      </div>
      {isUser && <span className="chat-bubble__status">{message.seenByAdmin ? 'Seen' : 'Sent'}</span>}
    </div>
  );
}

function ChatThread() {
  const { messages, adminTyping, sendMessage, setUserTyping } = useChat();
  const [text, setText] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, adminTyping]);

  const handleTextChange = (e) => {
    setText(e.target.value);
    setUserTyping(e.target.value.length > 0);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImageError('');
    try {
      const base64 = await compressImageToBase64(file);
      setPendingImage(base64);
    } catch (err) {
      setImageError(
        err instanceof ImageTooLargeError ? err.message : 'Could not attach that image. Please try a different file.'
      );
    }
  };

  const handleSend = async () => {
    if (!text.trim() && !pendingImage) return;
    setSending(true);
    try {
      await sendMessage({ text: text.trim(), imageBase64: pendingImage });
      setText('');
      setPendingImage(null);
      setUserTyping(false);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="chat-thread" ref={scrollRef}>
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} onImageClick={setPreviewImage} />
        ))}
        {adminTyping && (
          <div className="chat-bubble-row chat-bubble-row--admin">
            <div className="chat-bubble chat-bubble--admin chat-bubble--typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
      </div>

      {imageError && <p className="chat-widget__error">{imageError}</p>}

      {pendingImage && (
        <div className="chat-pending-image">
          <img src={pendingImage} alt="Pending attachment" />
          <button onClick={() => setPendingImage(null)} aria-label="Remove attachment">×</button>
        </div>
      )}

      <div className="chat-composer">
        <button className="chat-composer__attach" onClick={() => fileInputRef.current?.click()} aria-label="Attach image" type="button">
          📎
        </button>
        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
        <input
          className="chat-composer__input"
          placeholder="Type a reply..."
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onBlur={() => setUserTyping(false)}
        />
        <button className="chat-composer__send" onClick={handleSend} disabled={sending || (!text.trim() && !pendingImage)} aria-label="Send message">
          ↑
        </button>
      </div>

      {previewImage && (
        <div className="chat-lightbox" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} alt="Full size attachment" />
        </div>
      )}
    </>
  );
}

function ChatWidget() {
  const { isOpen, close, view, startSession } = useChat();
  const logoUrl = `${process.env.PUBLIC_URL}/images/flarewhite.jpg`;

  if (!isOpen) return null;

  return (
    <div className="chat-widget-wrapper">
      <div className="chat-widget">
        <div className="chat-widget__header">
          <span className="chat-widget__header-title">
            {view === 'chat' && <img src={logoUrl} alt="" className="chat-widget__header-logo" />}
            {view === 'form' ? 'Contact us' : 'Live Support Chat'}
            {view === 'chat' && <span className="chat-widget__online-dot" />}
          </span>
          <button className="chat-widget__close" onClick={close} aria-label="Close chat">×</button>
        </div>

        <div className="chat-widget__body">
          {view === 'form' ? <ContactForm onSubmit={startSession} /> : <ChatThread />}
        </div>
      </div>

      <button className="chat-widget__close-chat-btn" onClick={close}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="5" y1="5" x2="19" y2="19" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="19" y1="5" x2="5" y2="19" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        Close chat
      </button>
    </div>
  );
}

export default ChatWidget;